/** The class each image's rule is keyed on, by the image's URL and srcset. */
const classNames = new Map();
let sheet;
let imageSetSupported;

/**
 * The icon classes of a model whose Font Awesome icon can be replaced by an
 * uploaded image, such as a group or a tag: `icon` unchanged when there is no
 * image, and with `IconImage IconImage--<n>` added when there is one (and
 * `IconImage--only` when there's no icon besides the image).
 *
 * Whatever renders the classes then shows the image, without knowing about
 * it: the Icon component, a Button's or a Badge's `icon`, even a plain
 * `<i className={icon}>`. The first time an image is seen, a rule for its
 * class is added to the page, setting `--icon-image`, which IconImage.less
 * paints in the glyph's place.
 *
 * A model with an icon image overrides its icon getter with it:
 *
 *     icon() {
 *       return withIconImage(Model.attribute<string | null>('icon').call(this), this.iconImageUrl(), this.iconImageSrcset());
 *     }
 */
export default function withIconImage(icon, url, srcset) {
  if (!url) return icon;
  return [icon, 'IconImage', iconImageClass(url, srcset), !icon && 'IconImage--only'].filter(Boolean).join(' ');
}
function iconImageClass(url, srcset) {
  const key = `${url}\n${srcset || ''}`;
  let className = classNames.get(key);
  if (!className) {
    className = `IconImage--${classNames.size + 1}`;
    classNames.set(key, className);
    addRules(className, imageValue(url, srcset));
  }
  return className;
}
function addRules(className, image) {
  const stylesheet = getStylesheet();
  if (!stylesheet) return;
  const declaration = `{ --icon-image: ${image}; }`;

  // Separate rules, so that a browser without :has() still shows the image,
  // in the badge's icon slot rather than filling the badge.
  for (const selector of [`.${className}`, `.Badge:has(> .${className})`]) {
    try {
      stylesheet.insertRule(`${selector} ${declaration}`, stylesheet.cssRules.length);
    } catch {
      // An unsupported selector: that rule only.
    }
  }
}
function getStylesheet() {
  if (sheet === undefined) {
    if (typeof document === 'undefined') return null;
    const style = document.createElement('style');
    style.setAttribute('data-icon-images', '');
    document.head.appendChild(style);
    sheet = style.sheet;
  }
  return sheet;
}

/**
 * The CSS image: an image-set() of the srcset's density variants, or the
 * plain URL.
 */
function imageValue(url, srcset) {
  const candidates = (srcset || '').split(/,\s+/).map(candidate => candidate.trim().split(/\s+/)).filter(_ref => {
    let [src] = _ref;
    return src;
  });

  // image-set() takes densities only, not srcset's width descriptors.
  if (candidates.length && candidates.every(_ref2 => {
    let [, density = '1x'] = _ref2;
    return /^\d+(\.\d+)?x$/.test(density);
  }) && supportsImageSet()) {
    return `image-set(${candidates.map(_ref3 => {
      let [src, density = '1x'] = _ref3;
      return `${cssUrl(src)} ${density}`;
    }).join(', ')})`;
  }
  return cssUrl(url);
}
function supportsImageSet() {
  if (imageSetSupported === undefined) {
    imageSetSupported = typeof CSS !== 'undefined' && !!CSS.supports?.('background-image', 'image-set(url("x.png") 1x)');
  }
  return imageSetSupported;
}
function cssUrl(url) {
  return `url("${url.replace(/[\n\r\f]/g, '').replace(/["\\]/g, '\\$&')}")`;
}
flarum.reg.add('core', 'common/utils/withIconImage', withIconImage);